import * as z from 'zod'
import { zodQs } from '../utils'
import type { UIMessage, LanguageModelUsage } from 'ai'
import { ConversationAnalysisSchema } from './ConversationAnalysis'
import {
    AgentBuiltInTool,
    AgentGatewayOptionsSchema,
    AgentReasoningSchema,
    AgentWebSearchSchema,
    AgentSearchMode,
    AgentRankingSchema,
} from './Agent'
import { MIME_TYPE_REGEX } from './Media'

export const DeviceContextSchema = z.object({
    dateTime: z.string().optional(),
    timezone: z.string().optional(),
    language: z.string().optional(),
    languages: z.array(z.string()).optional(),
    url: z.string().optional(),
    pathname: z.string().optional(),
    pageTitle: z.string().optional(),
    referrer: z.string().optional(),
    screenWidth: z.number().optional(),
    screenHeight: z.number().optional(),
    viewportWidth: z.number().optional(),
    viewportHeight: z.number().optional(),
    devicePixelRatio: z.number().optional(),
    maxTouchPoints: z.number().optional(),
    hardwareConcurrency: z.number().optional(),
    deviceMemory: z.number().optional(),
    platform: z.string().optional(),
    connectionType: z.string().optional(),
    connectionEffectiveType: z.string().optional(),
    connectionDownlink: z.number().optional(),
    connectionRtt: z.number().optional(),
    saveData: z.boolean().optional(),
    darkMode: z.boolean().optional(),
    reducedMotion: z.boolean().optional(),
    historyLength: z.number().optional(),
    geolocationLatitude: z.number().optional(),
    geolocationLongitude: z.number().optional(),
    geolocationAccuracy: z.number().optional(),
    geolocationCity: z.string().optional(),
})

export type DeviceContext = z.infer<typeof DeviceContextSchema>

const TextPartSchema = z.object({
    type: z.literal('text'),
    text: z.string(),
})

const FilePartSchema = z.object({
    type: z.literal('file'),
    mediaType: z.string().regex(MIME_TYPE_REGEX),
    url: z.url(),
    filename: z.string().optional(),
    providerMetadata: z
        .record(z.string(), z.record(z.string(), z.unknown()))
        .optional(),
})

const MessagePartSchema = z.discriminatedUnion('type', [
    TextPartSchema,
    FilePartSchema,
])

export const ChatRequestSchema = z.object({
    id: z.string(),
    messages: z.array(
        z.object({
            id: z.string().optional(),
            role: z.enum(['assistant', 'user']),
            content: z.string(),
            parts: z.array(MessagePartSchema),
            createdAt: z.string().optional(),
        }),
    ),
    model: z.string().optional(),
    temperature: z.number().optional(),
    prompt: z.string().optional(),
    maxOutputTokens: z.number().positive().optional(),
    presencePenalty: z.number().min(-2).max(2).optional(),
    frequencyPenalty: z.number().min(-2).max(2).optional(),
    searchMode: z.enum(AgentSearchMode).optional(),
    limit: z.number().optional(),
    ranking: AgentRankingSchema.optional(),
    reasoning: AgentReasoningSchema,
    webSearch: AgentWebSearchSchema.optional(),
    maxStepCount: z.number().int().min(1).max(40).optional(),
    disabledTools: z.array(z.enum(AgentBuiltInTool)).optional(),
    fallbackModels: z.array(z.string()).optional(),
    gatewayOptions: AgentGatewayOptionsSchema.partial().optional(),
    deviceContext: DeviceContextSchema.optional(),
    externalContext: z.record(z.string(), z.unknown()).optional(),
    smoothStream: z.boolean().optional(),
})

export type ChatRequest = z.infer<typeof ChatRequestSchema>

export enum BlockedReasonCode {
    SevereAbuse = 'severe_abuse',
    HighRiskScore = 'high_risk_score',
    RepeatedWarnings = 'repeated_warnings',
}

export type ChatMetadata = {
    ipAddress: string
    origin: string
    userAgent: string
}

export type ChatMessageActions =
    | 'show-info'
    | 'revise'
    | 'regenerate'
    | 'upvote'
    | 'downvote'
    | 'feedback'

export type Chat = z.infer<typeof ChatSchema>
export type ChatsQuerystring = z.infer<typeof ChatsQuerystringSchema>

export type UIChatMessageDeviceContext = DeviceContext

export type UIChatMessageMetadata = {
    /** Unix timestamp (ms) when the stream started */
    createdAt?: number
    /** Unix timestamp (ms) when the stream finished */
    completedAt?: number
    /** Canonical model ID used for the request (e.g. `google/gemini-3-flash`) */
    model?: string
    /** Total tokens consumed across all steps */
    totalTokens?: number
    /** Per-tool-call durations in ms, keyed by tool call ID */
    partTimings?: Record<string, number>
    /** Client device context sent with the request */
    deviceContext?: UIChatMessageDeviceContext
    /** External context provided by the host application and validated server-side */
    externalContext?: Record<string, unknown>
    /** Provider that ultimately served the request (e.g. `vertex`, `openai`) */
    resolvedProvider?: string
    /** Actual model ID as exposed by the resolved provider (e.g. `gemini-3-flash-preview`) */
    resolvedModelId?: string
    /** Market cost of the request in USD, as a numeric string */
    cost?: string
    /** Number of model-level routing attempts made by the gateway */
    modelAttemptCount?: number
    /** Total number of provider-level attempts (includes retries across providers) */
    totalProviderAttemptCount?: number
    /** Whether Zero Data Retention was active for this request */
    zeroDataRetention?: boolean
    /** Whether the provider was instructed not to use the prompt for model training */
    disallowPromptTraining?: boolean
    /** Vercel AI Gateway generation ID, useful for tracing in the Vercel dashboard */
    generationId?: string
    /** Tokens served from the provider's prompt cache (e.g. Gemini context caching) */
    cachedTokens?: number
    /** Provider traffic tier (e.g. `PROVISIONED_THROUGHPUT`, `ON_DEMAND`) */
    trafficType?: string
}

export type UIChatMessage = UIMessage & {
    metadata?: UIChatMessageMetadata
}

export const ChatSchema = z.object({
    id: z.string(),
    messages: z.array(z.unknown()) as unknown as z.ZodType<UIChatMessage[]>,
    title: z.string().nullable(),
    summary: z.string().nullable(),
    messageCountAtLastSummary: z.string().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    metadata: z.record(z.string(), z.unknown()),
    totalCostInUSD: z.string(),
    usage: z.unknown() as unknown as z.ZodType<LanguageModelUsage>,
    agentId: z.string().nullable(),
    userId: z.string(),
    organizationId: z.string(),
    deleted: z.boolean(),
    deletedAt: z.coerce.date().nullable(),
    deletedBy: z.string().nullable(),
    conversationAnalysis: ConversationAnalysisSchema.nullable().optional(),
    blocked: z.boolean(),
    blockedAt: z.coerce.date().nullable(),
    blockedReasonCode: z.enum(BlockedReasonCode).nullable(),
    blockedReason: z.string().nullable(),
    warningCount: z.number(),
    lastMessageAt: z.coerce.date(),
})

export const ChatsQuerystringSchema = z.object({
    ...zodQs.pagination(),
    ...zodQs.sort(['updatedAt', 'createdAt', 'lastMessageAt']),
    ...zodQs.filters(['agentId', 'userId']),
    ...zodQs.range('updatedAt'),
    ...zodQs.range('lastMessageAt'),
    ...zodQs.ids(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
})

export const ChatsPublicSchema = z.object({
    id: z.string(),
    agentId: z.string().optional(),
    organizationId: z.string(),
    summary: z.string().optional(),
    title: z.string().optional(),
    userId: z.string(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    lastMessageAt: z.coerce.date(),
    messages: z.array(z.unknown()),
    blocked: z.boolean(),
})

export type ChatPublic = z.infer<typeof ChatsPublicSchema>
